<script setup>
import { ref } from "vue";
import Modal from "@/components/modals/Modal.vue";

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['applyPattern', 'close']);

const patterns = [
  {
    id: 'qa-basic',
    name: 'Basic QA Rubric',
    description: 'Correctness + grounding + clarity',
    icon: '❓',
    criteria: [
      {
        id: crypto.randomUUID(),
        name: 'Core correctness',
        type: 'binary',
        description: 'Is the answer factually correct?',
        weight: 40,
        passDescription: 'Answer is factually accurate and addresses the question',
        failDescription: 'Answer contains factual errors or misses the question',
        strictBinary: true
      },
      {
        id: crypto.randomUUID(),
        name: 'Grounding to references',
        type: 'semantic',
        description: 'How well does the answer use provided references?',
        weight: 30,
        semanticType: 'grounding',
        semanticPrompt: 'Evaluate how well this response is grounded in the provided reference materials. Check for accuracy and proper citations.',
        semanticScale: '0-2',
        showExamplesToJudge: false
      },
      {
        id: crypto.randomUUID(),
        name: 'Clarity',
        type: 'ordinal',
        description: 'Is the answer clear and well-structured?',
        weight: 30,
        ordinalLevels: [
          { score: 0, label: 'Unclear', description: 'Response is confusing or poorly organized' },
          { score: 1, label: 'Acceptable', description: 'Response is understandable but could be clearer' },
          { score: 2, label: 'Excellent', description: 'Response is clear, well-organized, and easy to understand' }
        ]
      }
    ]
  },
  {
    id: 'coding',
    name: 'Coding Rubric',
    description: 'Tests + style + safety',
    icon: '💻',
    criteria: [
      {
        id: crypto.randomUUID(),
        name: 'Tests pass',
        type: 'binary',
        description: 'Do all unit tests pass?',
        weight: 50,
        passDescription: 'All unit tests pass successfully',
        failDescription: 'One or more unit tests fail',
        strictBinary: true
      },
      {
        id: crypto.randomUUID(),
        name: 'Code style',
        type: 'ordinal',
        description: 'Is the code well-formatted and idiomatic?',
        weight: 25,
        ordinalLevels: [
          { score: 0, label: 'Poor', description: 'Code is messy, inconsistent, or violates style guidelines' },
          { score: 1, label: 'Good', description: 'Code follows most style guidelines with minor issues' },
          { score: 2, label: 'Excellent', description: 'Code is clean, consistent, and follows all style guidelines' }
        ]
      },
      {
        id: crypto.randomUUID(),
        name: 'Safety',
        type: 'semantic',
        description: 'Does the code avoid common security issues?',
        weight: 25,
        semanticType: 'safety',
        semanticPrompt: 'Evaluate whether this code avoids common security vulnerabilities (e.g., SQL injection, XSS, buffer overflows). Check for proper input validation and safe API usage.',
        semanticScale: '0-2',
        showExamplesToJudge: false
      }
    ]
  },
  {
    id: 'summarization',
    name: 'Summarization Rubric',
    description: 'Coverage + faithfulness + style',
    icon: '📝',
    criteria: [
      {
        id: crypto.randomUUID(),
        name: 'Coverage',
        type: 'ordinal',
        description: 'Does the summary capture key points?',
        weight: 40,
        ordinalLevels: [
          { score: 0, label: 'Incomplete', description: 'Summary misses major key points' },
          { score: 1, label: 'Partial', description: 'Summary captures most key points but misses some' },
          { score: 2, label: 'Complete', description: 'Summary captures all key points comprehensively' }
        ]
      },
      {
        id: crypto.randomUUID(),
        name: 'Faithfulness',
        type: 'semantic',
        description: 'Is the summary faithful to the source?',
        weight: 40,
        semanticType: 'grounding',
        semanticPrompt: 'Evaluate whether this summary is faithful to the source material. Check that it does not add information not present in the source or misrepresent the content.',
        semanticScale: '0-2',
        showExamplesToJudge: false
      },
      {
        id: crypto.randomUUID(),
        name: 'Conciseness',
        type: 'ordinal',
        description: 'Is the summary appropriately concise?',
        weight: 20,
        ordinalLevels: [
          { score: 0, label: 'Too verbose', description: 'Summary is unnecessarily long and repetitive' },
          { score: 1, label: 'Acceptable', description: 'Summary is reasonably concise' },
          { score: 2, label: 'Excellent', description: 'Summary is concise and to the point' }
        ]
      }
    ]
  },
  {
    id: 'reasoning',
    name: 'Reasoning Rubric',
    description: 'Logic + depth + clarity',
    icon: '🧠',
    criteria: [
      {
        id: crypto.randomUUID(),
        name: 'Logical consistency',
        type: 'ordinal',
        description: 'Is the reasoning logically sound?',
        weight: 40,
        ordinalLevels: [
          { score: 0, label: 'Flawed', description: 'Contains logical errors or contradictions' },
          { score: 1, label: 'Mostly sound', description: 'Reasoning is generally logical with minor gaps' },
          { score: 2, label: 'Sound', description: 'Reasoning is fully logical and consistent' }
        ]
      },
      {
        id: crypto.randomUUID(),
        name: 'Depth of analysis',
        type: 'semantic',
        description: 'How thorough is the analysis?',
        weight: 40,
        semanticType: 'helpfulness',
        semanticPrompt: 'Evaluate the depth and thoroughness of this analysis. Consider whether it explores relevant nuances, considers multiple perspectives, and provides sufficient detail.',
        semanticScale: '0-2',
        showExamplesToJudge: false
      },
      {
        id: crypto.randomUUID(),
        name: 'Explanation clarity',
        type: 'ordinal',
        description: 'Is the reasoning clearly explained?',
        weight: 20,
        ordinalLevels: [
          { score: 0, label: 'Unclear', description: 'Reasoning steps are hard to follow' },
          { score: 1, label: 'Clear', description: 'Reasoning is explained clearly' },
          { score: 2, label: 'Very clear', description: 'Reasoning is exceptionally clear and easy to follow' }
        ]
      }
    ]
  }
];

const selectedPattern = ref(null);

function selectPattern(pattern) {
  selectedPattern.value = pattern;
}

function apply() {
  if (selectedPattern.value) {
    emit('applyPattern', selectedPattern.value);
  }
}
</script>

<template>
  <Modal
    :isOpen="isOpen"
    title="Rubric Pattern Library"
    size="3xl"
    :closeOnClickOutside="true"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-4">
      <p class="text-white/70 text-sm">
        Choose a rubric template to pre-populate grading criteria with sensible defaults.
      </p>
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div 
          v-for="pattern in patterns" 
          :key="pattern.id" 
          :class="[
            'bg-echoes-background-black border-2 rounded-lg p-3 cursor-pointer',
            selectedPattern?.id === pattern.id 
              ? 'border-echoes-primary ' 
              : 'border-echoes-border-gray'
          ]"
          @click="selectPattern(pattern)"
        >
          <div class="flex items-start gap-3">
            <div class="text-2xl">{{ pattern.icon }}</div>
            
            <div class="flex-1">
              <div class="flex items-start justify-between gap-2">
                <h3 class="text-white text-sm font-medium">{{ pattern.name }}</h3>
                <i 
                  v-if="selectedPattern?.id === pattern.id" 
                  class="uil uil-check text-echoes-primary text-sm"
                ></i>
              </div>
              <p class="text-white/60 text-xs mt-1">{{ pattern.description }}</p>
              <span class="text-white/50 text-xs mt-2 inline-block">{{ pattern.criteria.length }} criteria</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div class="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-echoes-border-gray">
      <button 
        class="px-4 py-2 rounded-lg text-sm text-white/70"
        @click="emit('close')"
      >
        Cancel
      </button>
      <button 
        :class="[
          'px-4 py-2 rounded-lg text-sm',
          selectedPattern 
            ? 'bg-echoes-primary text-white' 
            : 'bg-echoes-primary/30 text-white/50 cursor-not-allowed'
        ]"
        @click="apply" 
        :disabled="!selectedPattern"
      >
        Apply Pattern
      </button>
    </div>
  </Modal>
</template>




